import { createSlice } from "@reduxjs/toolkit";
import dayjs from "dayjs";

import { ZoneOption } from "@/interfaces/IZone";
import {
  createServiceUserAsync,
  deleteServiceUserAsync,
  getMoreServiceUsersAsync,
  getOneServiceUserAsync,
  getServiceUsersAsync,
  getZoneOptionsAsync,
  updateServiceUserAsync,
} from "../actions/service-users/service-users";
import { ReducerState } from "./staff";

interface ServiceUsersReducerState {
  // Define your reducer state here
  selectedServiceUser: string | null;
  serviceUsers: ReducerState;
  filters: {
    page: number;
    query: string;
    limit: number;
    zone: Array<string>;
    status: Array<string>;
  };
  statusCount: Record<string, number>;
  zoneOptions: {
    data: ZoneOption[];
    unallocated: number;
  };
  marCSVLoading: boolean;
}

const initialState: ServiceUsersReducerState = {
  // Set initial state here
  selectedServiceUser: null,
  serviceUsers: {
    data: [],
    loading: false,
    error: null,
    hasMoreData: true,
  },
  filters: {
    page: 1,
    query: "",
    limit: 10,
    zone: [],
    status: ["active"],
  },
  statusCount: {},
  zoneOptions: {
    data: [],
    unallocated: 0,
  },
  marCSVLoading: false,
};

export const serviceUsersReducer = createSlice({
  name: "serviceUsersReducer",
  initialState,
  reducers: {
    // Define service users reducer actions here
    setHasMoreData: (state, action) => {
      // Update state based on the action
      state.serviceUsers.hasMoreData = action.payload;
    },
    setSelectedServiceUser: (state, action) => {
      // Update state based on the action
      state.selectedServiceUser = action.payload;
    },
    addServiceUser: (state, action) => {
      const ifExists = state.serviceUsers.data.find(
        (item: any) => item._id === action.payload._id
      );
      if (!ifExists && action.payload?.maintainPosition) {
        const newData = [action.payload, ...state.serviceUsers.data];

        const sorted = newData.sort((a: any, b: any) => {
          return dayjs(b.createdAt).diff(dayjs(a.createdAt));
        });

        state.serviceUsers.data = sorted;
      } else if (!ifExists) {
        state.serviceUsers.data.unshift(action.payload);
      }
    },
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    setStatusCount: (state, action) => {
      state.statusCount = action.payload;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    setMarCSVLoading: (state, action) => {
      state.marCSVLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // zone options reducer
      .addCase(getZoneOptionsAsync.fulfilled, (state, action) => {
        state.zoneOptions.data = action.payload?.data;
        state.zoneOptions.unallocated = action.payload?.unallocated;
      })
      // service users reducer
      .addCase(getMoreServiceUsersAsync.pending, (state) => {
        state.serviceUsers.hasMoreData = true;
        state.serviceUsers.loading = true;
        state.serviceUsers.error = null;
      })
      .addCase(getMoreServiceUsersAsync.fulfilled, (state, action) => {
        state.serviceUsers.loading = false;
        const parsedData = JSON.parse(JSON.stringify(action.payload?.data));
        if (parsedData.length === 0) {
          state.serviceUsers.hasMoreData = false;
        }

        const notAlreadyPresent = parsedData.filter(
          (item: any) =>
            !state.serviceUsers.data.some(
              (existingItem: any) => existingItem._id === item._id
            )
        );

        state.serviceUsers.data = [
          ...state.serviceUsers.data,
          ...notAlreadyPresent,
        ];
      })
      .addCase(getMoreServiceUsersAsync.rejected, (state, action) => {
        state.serviceUsers.loading = false;
        state.serviceUsers.error =
          action.error.message || "Failed to fetch data";
      })
      .addCase(getServiceUsersAsync.pending, (state) => {
        state.serviceUsers.loading = true;
        state.serviceUsers.error = null;
      })
      .addCase(getServiceUsersAsync.fulfilled, (state, action) => {
        state.serviceUsers.loading = false;
        const parsedData = JSON.parse(JSON.stringify(action.payload?.data));
        if (parsedData.length === 0) {
          state.serviceUsers.hasMoreData = false;
        }
        state.serviceUsers.data = action.payload?.data;
        state.statusCount = action.payload?.statusCounts;
      })
      .addCase(getServiceUsersAsync.rejected, (state, action) => {
        state.serviceUsers.loading = false;
        state.serviceUsers.error =
          action.error.message || "Failed to fetch data";
      })

      .addCase(getOneServiceUserAsync.fulfilled, (state, action) => {
        const { _id } = action.payload?.data;
        const ifExists = state.serviceUsers.data.find(
          (item: any) => item._id === _id
        );
        if (!ifExists) {
          state.serviceUsers.data.push(action.payload?.data);
        }
      })

      .addCase(createServiceUserAsync.pending, (state) => {
        state.serviceUsers.loading = true;
        state.serviceUsers.error = null;
      })
      .addCase(createServiceUserAsync.fulfilled, (state, action) => {
        state.serviceUsers.loading = false;
        state.serviceUsers.data = [
          ...state.serviceUsers.data,
          action.payload?.data,
        ];
      })
      .addCase(createServiceUserAsync.rejected, (state, action) => {
        state.serviceUsers.loading = false;
        state.serviceUsers.error =
          action.error.message || "Failed to fetch data";
      })
      .addCase(updateServiceUserAsync.pending, (state) => {
        state.serviceUsers.loading = true;
        state.serviceUsers.error = null;
      })
      .addCase(updateServiceUserAsync.fulfilled, (state, action) => {
        state.serviceUsers.loading = false;
        const { _id } = action.payload?.data;
        const updatedData = state.serviceUsers.data.map((item: any) =>
          item._id === _id ? action.payload?.data : item
        );
        state.serviceUsers.data = updatedData;
      })
      .addCase(updateServiceUserAsync.rejected, (state, action) => {
        state.serviceUsers.loading = false;
        state.serviceUsers.error =
          action.error.message || "Failed to fetch data";
      })
      .addCase(deleteServiceUserAsync.pending, (state) => {
        state.serviceUsers.loading = true;
        state.serviceUsers.error = null;
      })
      .addCase(deleteServiceUserAsync.fulfilled, (state, action) => {
        state.serviceUsers.loading = false;
        const { _id } = action.payload?.data;
        const updatedData = state.serviceUsers.data.filter(
          (item: any) => item._id !== _id
        );
        state.serviceUsers.data = updatedData;
      })
      .addCase(deleteServiceUserAsync.rejected, (state, action) => {
        state.serviceUsers.loading = false;
        state.serviceUsers.error =
          action.error.message || "Failed to fetch data";
      });
  },
});

export const {
  setSelectedServiceUser,
  addServiceUser,
  setHasMoreData,
  setFilters,
  resetFilters,
  setStatusCount,
  setMarCSVLoading,
} = serviceUsersReducer.actions;
export default serviceUsersReducer.reducer;
